import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Key, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { PasswordField } from "@/components/auth/PasswordField";

const passwordSchema = z
  .string()
  .min(8, { message: "Password must be at least 8 characters" })
  .regex(/[A-Z]/, {
    message: "Password must contain at least 1 uppercase letter",
  })
  .regex(/[a-z]/, {
    message: "Password must contain at least 1 lowercase letter",
  })
  .regex(/[0-9]/, { message: "Password must contain at least 1 number" })
  .regex(/[^A-Za-z0-9]/, {
    message: "Password must contain at least 1 special character",
  });

const formSchema = z
  .object({
    password: passwordSchema,
    confirmPassword: z
      .string()
      .min(1, { message: "Confirm password is required" }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

type FormValues = z.infer<typeof formSchema>;

const ResetPassword = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const { user, inRecovery, updatePassword } = useAuth();
  const navigate = useNavigate();

  console.log("[ResetPassword] Component state:", {
    hasUser: !!user,
    inRecovery,
    userEmail: user?.email,
    timestamp: new Date().toISOString(),
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  const onSubmit = async (data: FormValues) => {
    if (!inRecovery) {
      setError(
        "Invalid password reset session. Please request a new reset link.",
      );
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      console.log(
        "[ResetPassword] Updating password using Supabase updateUser",
      );

      // Simple updateUser call - Supabase handles the rest
      const { error: updateError } = await updatePassword(data.password);

      if (updateError) {
        console.error("[ResetPassword] Password update failed:", updateError);
        setError("Failed to update password. Please try again.");
        return;
      }

      console.log("[ResetPassword] Password updated successfully");

      // Set redirecting state to show smooth transition
      setIsRedirecting(true);

      // Sign out to flush the one-time recovery session
      console.log("[ResetPassword] Signing out to flush recovery session");
      await supabase.auth.signOut();

      // Small delay to prevent flash before navigation
      await new Promise((resolve) => setTimeout(resolve, 300));

      // Redirect to login with success message
      navigate("/login", {
        state: {
          message:
            "Your password has been reset successfully. Please log in with your new password.",
        },
      });
    } catch (error) {
      console.error("[ResetPassword] Unexpected error:", error);
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Show redirecting state
  if (isRedirecting) {
    return (
      <div className="min-h-screen bg-gradient-to-t from-indigo-50 via-blue-50 to-white flex items-center justify-center p-4">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-amber-500" />
          <p className="text-slate-600">
            Password updated successfully! Redirecting to login...
          </p>
        </div>
      </div>
    );
  }

  // Show loading if not in recovery yet
  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-t from-indigo-50 via-blue-50 to-white flex items-center justify-center p-4">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-amber-500" />
          <p className="text-slate-600">Loading reset session...</p>
        </div>
      </div>
    );
  }

  // Show error if not in recovery mode
  if (!inRecovery) {
    return (
      <div className="min-h-screen bg-gradient-to-t from-indigo-50 via-blue-50 to-white flex items-center justify-center p-4 overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
          <div className="absolute -left-16 top-20 w-64 h-64 bg-gradient-to-br from-amber-200 to-yellow-300 rounded-full opacity-20 blur-3xl"></div>
          <div className="absolute right-10 bottom-1/3 w-96 h-96 bg-gradient-to-tl from-amber-400 to-amber-200 rounded-full opacity-10 blur-3xl"></div>
          <div className="absolute left-1/3 bottom-0 w-72 h-72 bg-gradient-to-tr from-blue-200 to-cyan-200 rounded-full opacity-10 blur-3xl"></div>
        </div>

        <Card className="w-full max-w-md border-0 bg-white/70 backdrop-blur-sm shadow-xl z-10">
          <CardHeader className="text-center space-y-2">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-gradient-to-r from-red-400 to-red-500 mb-4">
              <Key className="h-8 w-8 text-white" />
            </div>
            <CardTitle className="text-2xl font-bold">
              Invalid Reset Link
            </CardTitle>
            <CardDescription className="text-base">
              The password reset link is invalid or has expired
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-center text-slate-600">
              Please request a new password reset link to continue.
            </p>
            <Button
              onClick={() => navigate("/forgot-password")}
              className="w-full bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white"
            >
              Request New Reset Link
            </Button>
            <div className="text-center">
              <Link
                to="/login"
                className="text-sm text-amber-600 hover:text-amber-700 hover:underline"
              >
                Back to login
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show form - user is authenticated and in recovery mode
  return (
    <div className="min-h-screen bg-gradient-to-t from-indigo-50 via-blue-50 to-white flex items-center justify-center p-4 overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
        <div className="absolute -left-16 top-20 w-64 h-64 bg-gradient-to-br from-amber-200 to-yellow-300 rounded-full opacity-20 blur-3xl"></div>
        <div className="absolute right-10 bottom-1/3 w-96 h-96 bg-gradient-to-tl from-amber-400 to-amber-200 rounded-full opacity-10 blur-3xl"></div>
        <div className="absolute left-1/3 bottom-0 w-72 h-72 bg-gradient-to-tr from-blue-200 to-cyan-200 rounded-full opacity-10 blur-3xl"></div>
      </div>

      {/* Main content container with 75% width and height */}
      <div className="w-[75%] h-[75vh] bg-gradient-to-t from-amber-100/70 via-amber-50/60 to-white/90 backdrop-blur-sm rounded-3xl shadow-2xl flex overflow-hidden z-10">
        {/* Left side with image - hidden on small screens */}
        <div className="hidden md:flex md:w-1/2 bg-gradient-to-b from-indigo-900/5 to-indigo-900/30 items-center justify-center p-8 relative">
          <div className="absolute inset-0 z-0 opacity-20">
            {/* Decorative grid pattern */}
            <div
              className="absolute inset-0"
              style={{
                backgroundImage:
                  "radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px)",
                backgroundSize: "24px 24px",
              }}
            ></div>
          </div>
          <div className="relative z-10 flex flex-col items-center">
            <img
              src="/team-ai-headsets.png"
              alt="AI Team"
              className="w-full max-w-md object-contain rounded-xl shadow-lg"
              onError={(e) => {
                // Fallback if image doesn't load
                e.currentTarget.src = "/illustrations/team.png";
              }}
            />
            <div className="mt-8 text-center">
              <h2 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-amber-500 to-yellow-600">
                Secure Your Account
              </h2>
              <p className="mt-2 text-slate-600 max-w-sm">
                Create a strong password to protect your AI workforce and
                business data
              </p>
            </div>
          </div>
        </div>

        {/* Right side with password reset form */}
        <div className="w-full md:w-1/2 flex items-center justify-center p-4 md:p-8">
          <Card className="w-full max-w-md border-0 bg-white/70 backdrop-blur-sm shadow-xl">
            <CardHeader className="text-center space-y-2">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-gradient-to-r from-yellow-400 to-amber-500 mb-4">
                <Key className="h-8 w-8 text-white" />
              </div>
              <CardTitle className="text-2xl font-bold">
                Reset Your Password
              </CardTitle>
              <CardDescription className="text-base">
                Enter your new password to complete the reset process
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Error Display */}
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-5">
                  {error}
                </div>
              )}

              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="space-y-5"
                >
                  <PasswordField
                    form={form}
                    name="password"
                    label="New Password"
                    placeholder="Enter your new password"
                    disabled={isLoading}
                    autoComplete="new-password"
                  />

                  <PasswordField
                    form={form}
                    name="confirmPassword"
                    label="Confirm New Password"
                    placeholder="Confirm your new password"
                    disabled={isLoading}
                    autoComplete="new-password"
                  />

                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Updating Password...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Key className="h-4 w-4" />
                        Update Password
                      </span>
                    )}
                  </Button>
                </form>
              </Form>

              <div className="mt-6 text-center">
                <Link
                  to="/login"
                  className="text-sm text-amber-600 hover:text-amber-700 hover:underline"
                >
                  Back to login
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Debug Panel for Development */}
      {process.env.NODE_ENV === "development" && (
        <div className="fixed bottom-4 right-4 p-4 bg-black/80 text-white rounded-lg text-xs space-y-1 z-50 max-w-xs">
          <h3 className="font-bold mb-2">🔧 Debug Info:</h3>
          <div>Has User: {user ? "Yes" : "No"}</div>
          <div>In Recovery: {inRecovery ? "Yes" : "No"}</div>
          <div>User Email: {user?.email || "None"}</div>
          <div>URL Hash: {window.location.hash ? "Present" : "None"}</div>
          <div>Current Path: {window.location.pathname}</div>
        </div>
      )}
    </div>
  );
};

export default ResetPassword;
